import * as React from 'react';

import { PropertySource } from './property-source';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { BadgeGenerator } from 'components/common/badge-generation';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingAmenities } from 'components/search/listing/internals/listing-amenities';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPropertyBadges } from './listing-property-badges';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { SearchListingPlaceholderSmall } from './placeholders';
import { Stars } from './star-rating';
import { TitleHeading } from './title-heading';

interface DefaultListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  defaultListingTopMarginClass?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  imageAspectRatio?: string;
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  displaySecondaryBadge?: boolean;
  rfrr?: string;
}

export const DefaultListing: React.FunctionComponent<DefaultListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  className,
  defaultListingTopMarginClass,
  stars,
  getTitleComponent,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  imageAspectRatio,
  displaySecondaryBadge,
  rfrr,
}) => {
  const {
    amenities,
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    price,
    propertySummary,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    recentlyViewed,
    propertyDetailsLink,
    propertySource,
    pinnedDetails,
    propertyListingBadgeModel,
    travelAd,
  } = model;

  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
    tag: 'li' as 'li',
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };
  const propertySourceProps = propertySource;

  const priceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    wrapPrice: true,
    viewSmall: true,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    index,
  };
  const pinned = Boolean(pinnedDetails?.heading);

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <SearchListingPlaceholderSmall />
      </li>
    );
  }

  const { secondary } = { ...propertyListingBadgeModel };
  const secondaryIsSafe = secondary && (secondary.text || secondary.icon || secondary.mark);

  return (
    // Typescript doesn't seem to be enforcing props for this Card. Ref is not a prop in this version of UitkCard.
    <UitkCard {...listingWrapperProps}>
      <ListingName visuallyHidden={true} />
      <div className="has-action listing__card">
        <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} travelAd={travelAd} pinnedDefaultListing={pinned} rfrr={rfrr} />
        {pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
        <UitkLayoutPosition type="relative">
          <ListingErrorBoundary thumbnail={thumbnail} name={name}>
            {getTitleComponent(imageAspectRatio)}
          </ListingErrorBoundary>
          <UitkLayoutPosition type="relative" cloneElement={true}>
            <UitkSpacing padding="three">
              <UitkCardContentSection className={`listing-content ${defaultListingTopMarginClass}`}>
                {displaySecondaryBadge && secondaryIsSafe && (
                  <UitkLayoutPosition aria-hidden={true} type="absolute" position={{ top: 'minus-four', right: 'two', zindex: 'layer3' }}>
                    <BadgeGenerator badge={secondary} uniqueId={model.hotelId} />
                  </UitkLayoutPosition>
                )}
                <UitkLayoutFlex alignItems="stretch" space="two">
                  <UitkLayoutFlexItem grow={1} minWidth="half_width">
                    <UitkLayoutFlex direction="column" justifyContent="space-between" className="uitk-type-300">
                      <UitkLayoutFlexItem>
                        <div>
                          <ListingPropertyBadges />
                          <ListingName lineCount={2} />
                          <Stars stars={stars} />
                          <PropertySource {...propertySourceProps} />
                          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                          <Neighborhood neighborhood={neighborhood} />
                          <ListingAmenities amenities={amenities} />
                        </div>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem>
                        <div>
                          <ListingCommonSection routeStoreGoBack={routeStoreGoBack} />
                        </div>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem alignSelf="end" maxWidth="half_width">
                    <div>
                      {displaySecondaryBadge && secondaryIsSafe && <div className="is-visually-hidden">{secondary.text}</div>}
                      <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                    </div>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </UitkCardContentSection>
            </UitkSpacing>
          </UitkLayoutPosition>
          <ListingFooter {...cardLinkProps} listingFooter={listingFooter} listingClickHandler={listingClickHandler} />
          {alternateDates && (
            <AdditionalInfo {...alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
              <AlternateDates {...alternateDatesProps} />
            </AdditionalInfo>
          )}
        </UitkLayoutPosition>
      </div>
      <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
    </UitkCard>
  );
};

DefaultListing.displayName = 'DefaultListing';
